import { useEffect } from 'react'

import Image from 'next/image'

import {
	useHandleNewSurveySubmissions,
	useNewSetNextButtonDisabled,
	useNewSurveyState,
	useNewSurveySubmissions
} from '@context/new-survey/useNewSurvey'

import type { SurveyQuestion } from '@lib/types/subscription-box'

type RadioSelectParams = {
	surveyQuestion: SurveyQuestion
	isRequired?: boolean
	index: number
}
export default function RadioSelect({ surveyQuestion, isRequired, index }: RadioSelectParams) {
	const { currentIndex } = useNewSurveyState()
	const surveySubmissions = useNewSurveySubmissions()
	const handleSurveySubmissions = useHandleNewSurveySubmissions()
	const setNextButtonDisabled = useNewSetNextButtonDisabled()

	// Set survey submissions on radio value change
	const handleValueChange = (optionValue: string, id: number) => {
		if (optionValue) {
			handleSurveySubmissions(surveyQuestion, {
				optionValue,
				id
			})
		}
	}

	// Handle next button disabled
	useEffect(() => {
		if (index === currentIndex) {
			if (isRequired && !surveySubmissions[currentIndex]?.response?.length) {
				setNextButtonDisabled(true)
			} else {
				setNextButtonDisabled(false)
			}
		}
	}, [surveySubmissions, currentIndex, isRequired, index])

	const hasImages = surveyQuestion?.surveyAnswerOptions?.some((opt) => !!opt.optionImage)

	if (index !== currentIndex) return null

	return (
		<div>
			{/* Survey question body */}
			{surveyQuestion?.body && (
				<h3 className="text-2xl md:text-3xl font-sans font-medium text-center">
					{surveyQuestion.body}
				</h3>
			)}

			{Array.isArray(surveyQuestion?.surveyAnswerOptions) && (
				<ul
					className={`self-center mt-8 mb-2 md:mt-10 ${
						hasImages
							? 'flex flex-wrap justify-center gap-y-4 md:gap-6 max-w-[800px] mx-auto'
							: 'flex flex-col w-full max-w-[500px] mx-auto gap-3'
					}`}
				>
					{surveyQuestion.surveyAnswerOptions.map(
						({ optionLabel, optionValue, optionImage, id }) => {
							const thisOptionIsSelected =
								surveySubmissions?.[currentIndex]?.response[0] === optionValue

							return (
								<li
									className={`${
										!hasImages
											? 'w-full'
											: 'w-[calc(50%-2rem)] md:w-[calc(33.333%-1rem)] lg:w-[calc(25%-1.125rem)] flex justify-center'
									}`}
									key={id}
								>
									{!optionImage ? (
										<button
											type="button"
											className={`w-full px-6 py-4 font-sans font-normal border rounded-lg transition-colors duration-200 ease-in-out text-base md:text-lg ${
												thisOptionIsSelected
													? 'border-bbx-spark bg-bbx-spark/10 text-bbx-style'
													: 'border-gray-300 bg-white text-gray-600'
											}`}
											onClick={() => handleValueChange(optionValue, id)}
										>
											{optionLabel}
										</button>
									) : (
										<button
											type="button"
											className="flex flex-col items-center gap-3 md:gap-4"
											onClick={() => handleValueChange(optionValue, id)}
										>
											<Image
												className="rounded-full"
												src={optionImage}
												alt={`Survey question image: ${optionLabel}`}
												width={96}
												height={96}
											/>
											<div className="relative inline-block px-0.5 md:px-2.5 pb-1 md:pb-2 transition-colors duration-300 ease-in-out">
												{/* Option label */}
												{optionLabel && (
													<p
														className={`font-sans font-normal text-center text-sm md:text-sm ${
															thisOptionIsSelected ? '' : 'text-gray-450'
														}`}
													>
														{optionLabel}
													</p>
												)}
												{/* Border bottom when active */}
												<div
													aria-hidden="true"
													className={`absolute inset-x-0 bottom-0 h-0.5 bg-bbx-spark rounded-full transition-opacity duration-75 ease-in-out ${
														thisOptionIsSelected ? 'opacity-100' : 'opacity-0'
													}`}
												/>
											</div>
										</button>
									)}
								</li>
							)
						}
					)}
				</ul>
			)}
		</div>
	)
}
